import { useState, useEffect } from 'react';
import { FiMail, FiPhone, FiBriefcase, FiUser, FiCalendar, FiCheckCircle, FiXCircle, FiSearch, FiEye, FiX, FiMessageSquare, FiCheck } from 'react-icons/fi';
import { demoRequestsAPI } from '../../services/api';
import { useApp } from '../../context/AppContext';

const DemoRequestsManagement = () => {
  const { addNotification } = useApp();
  const [demoRequests, setDemoRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [contactedFilter, setContactedFilter] = useState('all'); // 'all', 'contacted', 'pending'
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [editingNotes, setEditingNotes] = useState(null);
  const [notesValue, setNotesValue] = useState('');

  useEffect(() => {
    fetchDemoRequests();
  }, []);

  const fetchDemoRequests = async () => {
    try {
      setLoading(true);
      const data = await demoRequestsAPI.getAll();
      setDemoRequests(data);
    } catch (error) {
      showNotification('Error al cargar las solicitudes de demo', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleContacted = async (request) => {
    try {
      const newContacted = !request.contacted;
      await demoRequestsAPI.update(request.id, { contacted: newContacted });
      
      // Update local state
      setDemoRequests(prev => 
        prev.map(r => r.id === request.id 
          ? { ...r, contacted: newContacted, contacted_at: newContacted ? new Date().toISOString() : null }
          : r
        )
      );
      
      showNotification(
        newContacted ? 'Marcado como contactado' : 'Desmarcado como contactado',
        'success'
      );
    } catch (error) {
      showNotification('Error al actualizar el estado', 'error');
    }
  };

  const handleSaveNotes = async (requestId) => {
    try {
      await demoRequestsAPI.update(requestId, { notes: notesValue });
      
      // Update local state
      setDemoRequests(prev => 
        prev.map(r => r.id === requestId ? { ...r, notes: notesValue } : r)
      );
      
      setEditingNotes(null);
      setNotesValue('');
      showNotification('Notas guardadas', 'success');
    } catch (error) {
      showNotification('Error al guardar las notas', 'error');
    }
  };

  const openDetailsModal = (request) => {
    setSelectedRequest(request);
    setShowDetailsModal(true);
  };

  const startEditingNotes = (request) => {
    setEditingNotes(request.id);
    setNotesValue(request.notes || '');
  };

  const showNotification = (message, type) => {
    addNotification({ message, type });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getFilteredRequests = () => {
    return demoRequests.filter(request => {
      // Search filter
      const matchesSearch = !searchTerm ||
        request.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        request.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        request.company.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (request.phone && request.phone.includes(searchTerm));

      // Contacted filter
      let matchesContacted = true;
      if (contactedFilter === 'contacted') {
        matchesContacted = request.contacted;
      } else if (contactedFilter === 'pending') {
        matchesContacted = !request.contacted;
      }

      return matchesSearch && matchesContacted;
    });
  };

  const getStats = () => {
    const total = demoRequests.length;
    const contacted = demoRequests.filter(r => r.contacted).length;
    const pending = demoRequests.filter(r => !r.contacted).length;
    const emailsSent = demoRequests.filter(r => r.email_sent).length;
    const emailsFailed = demoRequests.filter(r => !r.email_sent).length;

    return { total, contacted, pending, emailsSent, emailsFailed };
  };

  const filteredRequests = getFilteredRequests();
  const stats = getStats();

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-full mx-auto px-4">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-gray-800">Solicitudes de Demo</h1>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm font-medium text-gray-600">Total</p>
            <p className="text-2xl font-semibold text-gray-900">{stats.total}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm font-medium text-gray-600">Pendientes</p>
            <p className="text-2xl font-semibold text-yellow-600">{stats.pending}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm font-medium text-gray-600">Contactados</p>
            <p className="text-2xl font-semibold text-green-600">{stats.contacted}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm font-medium text-gray-600">Emails enviados</p>
            <p className="text-2xl font-semibold text-blue-600">{stats.emailsSent}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm font-medium text-gray-600">Emails fallidos</p>
            <p className="text-2xl font-semibold text-red-600">{stats.emailsFailed}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Buscar por nombre, email, empresa o teléfono..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Status filters */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setContactedFilter('all')}
                className={`px-3 py-2 text-xs font-semibold rounded-full border transition-colors ${
                  contactedFilter === 'all'
                    ? 'bg-blue-100 text-blue-800 border-blue-200'
                    : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                }`}
              >
                Todas
              </button>
              <button
                onClick={() => setContactedFilter('pending')}
                className={`px-3 py-2 text-xs font-semibold rounded-full border transition-colors ${
                  contactedFilter === 'pending'
                    ? 'bg-yellow-100 text-yellow-800 border-yellow-200'
                    : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                }`}
              >
                Pendientes
              </button>
              <button
                onClick={() => setContactedFilter('contacted')}
                className={`px-3 py-2 text-xs font-semibold rounded-full border transition-colors ${
                  contactedFilter === 'contacted'
                    ? 'bg-green-100 text-green-800 border-green-200'
                    : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                }`}
              >
                Contactados
              </button>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {loading && demoRequests.length === 0 ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Cargando solicitudes...</p>
            </div>
          ) : filteredRequests.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <FiMail className="mx-auto text-4xl mb-2" />
              <p>{demoRequests.length === 0 ? 'No hay solicitudes de demo' : 'No se encontraron solicitudes con los filtros aplicados'}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Fecha
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Nombre
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Empresa
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Teléfono
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email enviado
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contactado
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Notas
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredRequests.map((request) => (
                    <tr key={request.id} className={!request.contacted ? 'bg-yellow-50' : ''}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center gap-2">
                          <FiCalendar className="text-gray-400" />
                          {formatDate(request.created_at)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <FiUser className="text-gray-400" />
                          <span className="text-sm font-medium text-gray-900">{request.name}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <a 
                          href={`mailto:${request.email}`}
                          className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                        >
                          <FiMail className="text-gray-400" />
                          {request.email}
                        </a>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <FiBriefcase className="text-gray-400" />
                          <span className="text-sm text-gray-900">{request.company}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {request.phone ? (
                          <a 
                            href={`tel:${request.phone}`}
                            className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                          >
                            <FiPhone className="text-gray-400" />
                            {request.phone}
                          </a>
                        ) : (
                          <span className="text-sm text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {request.email_sent ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                            <FiCheckCircle />
                            Sí
                          </span>
                        ) : (
                          <span 
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800 cursor-help"
                            title={request.email_error || 'Error desconocido'}
                          >
                            <FiXCircle />
                            No
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <label className="inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={request.contacted}
                            onChange={() => handleToggleContacted(request)}
                            className="sr-only peer"
                          />
                          <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                        </label>
                        {request.contacted && request.contacted_at && (
                          <div className="text-xs text-gray-500 mt-1">
                            {formatDate(request.contacted_at)}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {editingNotes === request.id ? (
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              value={notesValue}
                              onChange={(e) => setNotesValue(e.target.value)}
                              className="w-32 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                              placeholder="Notas..."
                              autoFocus
                            />
                            <button
                              onClick={() => handleSaveNotes(request.id)}
                              className="p-1 text-green-600 hover:text-green-800"
                              title="Guardar"
                            >
                              <FiCheck />
                            </button>
                            <button
                              onClick={() => { setEditingNotes(null); setNotesValue(''); }}
                              className="p-1 text-red-600 hover:text-red-800"
                              title="Cancelar"
                            >
                              <FiX />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => startEditingNotes(request)}
                            className="text-sm text-gray-600 hover:text-gray-800 flex items-center gap-1"
                            title={request.notes || 'Sin notas'}
                          >
                            <FiMessageSquare className="text-gray-400" />
                            {request.notes ? (
                              <span className="truncate max-w-[100px]">{request.notes}</span>
                            ) : (
                              <span className="text-gray-400 italic">Añadir nota</span>
                            )}
                          </button>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => openDetailsModal(request)}
                          className="text-blue-600 hover:text-blue-900 flex items-center gap-1"
                          title="Ver detalles"
                        >
                          <FiEye className="h-4 w-4" />
                          Ver
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Details Modal */}
        {showDetailsModal && selectedRequest && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold">Detalles de la Solicitud</h2>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="p-2 text-gray-500 hover:text-gray-700"
                >
                  <FiX className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500 uppercase font-medium">Nombre</p>
                    <p className="text-sm font-medium text-gray-900">{selectedRequest.name}</p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500 uppercase font-medium">Empresa</p>
                    <p className="text-sm font-medium text-gray-900">{selectedRequest.company}</p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500 uppercase font-medium">Email</p>
                    <a href={`mailto:${selectedRequest.email}`} className="text-sm text-blue-600 hover:text-blue-800">
                      {selectedRequest.email}
                    </a>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500 uppercase font-medium">Teléfono</p>
                    {selectedRequest.phone ? (
                      <a href={`tel:${selectedRequest.phone}`} className="text-sm text-blue-600 hover:text-blue-800">
                        {selectedRequest.phone}
                      </a>
                    ) : (
                      <p className="text-sm text-gray-400">No proporcionado</p>
                    )}
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500 uppercase font-medium">Fecha de solicitud</p>
                    <p className="text-sm font-medium text-gray-900">{formatDate(selectedRequest.created_at)}</p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500 uppercase font-medium">Email enviado</p>
                    {selectedRequest.email_sent ? (
                      <span className="inline-flex items-center gap-1 text-sm text-green-600">
                        <FiCheckCircle />
                        Sí
                      </span>
                    ) : (
                      <div>
                        <span className="inline-flex items-center gap-1 text-sm text-red-600">
                          <FiXCircle />
                          No
                        </span>
                        {selectedRequest.email_error && (
                          <p className="text-xs text-red-500 mt-1">{selectedRequest.email_error}</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {selectedRequest.message && (
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500 uppercase font-medium mb-2">Mensaje</p>
                    <p className="text-sm text-gray-900 whitespace-pre-wrap">{selectedRequest.message}</p>
                  </div>
                )}

                <div className="border-t pt-4">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Estado del contacto</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-500 uppercase font-medium">Contactado</p>
                      <div className="flex items-center gap-2 mt-1">
                        <label className="inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectedRequest.contacted}
                            onChange={() => {
                              handleToggleContacted(selectedRequest);
                              setSelectedRequest(prev => ({ ...prev, contacted: !prev.contacted }));
                            }}
                            className="sr-only peer"
                          />
                          <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                        </label>
                        <span className="text-sm text-gray-600">
                          {selectedRequest.contacted ? 'Sí' : 'No'}
                        </span>
                      </div>
                    </div>
                    {selectedRequest.contacted_at && (
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <p className="text-xs text-gray-500 uppercase font-medium">Contactado el</p>
                        <p className="text-sm text-gray-900">{formatDate(selectedRequest.contacted_at)}</p>
                        {selectedRequest.contacted_by && (
                          <p className="text-xs text-gray-500 mt-1">por {selectedRequest.contacted_by}</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 uppercase font-medium mb-2">Notas</p>
                  <textarea
                    value={selectedRequest.notes || ''}
                    onChange={(e) => setSelectedRequest(prev => ({ ...prev, notes: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Añadir notas sobre el contacto..."
                    rows={3}
                  />
                  <button
                    onClick={async () => {
                      try {
                        await demoRequestsAPI.update(selectedRequest.id, { notes: selectedRequest.notes });
                        setDemoRequests(prev => 
                          prev.map(r => r.id === selectedRequest.id ? { ...r, notes: selectedRequest.notes } : r)
                        );
                        showNotification('Notas guardadas', 'success');
                      } catch (error) {
                        showNotification('Error al guardar las notas', 'error');
                      }
                    }}
                    className="mt-2 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
                  >
                    Guardar notas
                  </button>
                </div>
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DemoRequestsManagement;
